import { PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';
import { noop } from 'lodash';

import { CompanySubscriptionSetSubscriptionCounts } from 'src/types';

interface CompanySubscriptionSetContextValue {
  currentSubscriptionCounts?: CompanySubscriptionSetSubscriptionCounts | undefined;
  setCurrentSubscriptionCounts: (subscriptionCounts: CompanySubscriptionSetSubscriptionCounts) => void;
  currentSubscriptionCountsLoading: boolean;
  setCurrentSubscriptionCountsLoading: (value: boolean) => void;
  currentSubscriptionCountsRefetch: () => void;
  setCurrentSubscriptionCountsRefetch: (fn: () => void) => void;
}

const companySubscriptionSetContextDefaultValue = {
  setCurrentSubscriptionCounts: noop,
  currentSubscriptionCountsLoading: false,
  setCurrentSubscriptionCountsLoading: noop,
  currentSubscriptionCountsRefetch: noop,
  setCurrentSubscriptionCountsRefetch: noop,
};

const companySubscriptionSetContext = createContext<CompanySubscriptionSetContextValue>(
  companySubscriptionSetContextDefaultValue,
);

export const CompanySubscriptionSetContextProvider = ({ children }: PropsWithChildren) => {
  const [currentSubscriptionCounts, setCurrentSubscriptionCounts] =
    useState<CompanySubscriptionSetSubscriptionCounts>();
  const [currentSubscriptionCountsLoading, setCurrentSubscriptionCountsLoading] = useState<boolean>(false);
  const [currentSubscriptionCountsRefetch, setCurrentSubscriptionCountsRefetch] = useState<() => void>(noop);

  const value = useMemo(() => {
    const result: CompanySubscriptionSetContextValue = {
      currentSubscriptionCounts,
      setCurrentSubscriptionCounts,
      currentSubscriptionCountsLoading,
      setCurrentSubscriptionCountsLoading,
      currentSubscriptionCountsRefetch,
      setCurrentSubscriptionCountsRefetch: (fn) => setCurrentSubscriptionCountsRefetch(() => fn),
    };
    return result;
  }, [currentSubscriptionCounts, currentSubscriptionCountsLoading, currentSubscriptionCountsRefetch]);

  return <companySubscriptionSetContext.Provider value={value}>{children}</companySubscriptionSetContext.Provider>;
};

export const useCompanySubscriptionSetContext = () => useContext(companySubscriptionSetContext);
